import {
  HOOD_MAP,
  SEARCH_PAGE_CITY_MAP,
  SearchHood,
  SearchPageCity
} from 'src/constants/searchPages'

/**
 * Resolve a free-form search "location" value into display-ready copy for the
 * "cerca de {X}" heading and related search surfaces.
 *
 * `filters.description` (and the raw `?description=` query param) is already a
 * human name on canonical /[pillar]/[city]/[hood] pages — SSR bakes
 * `cityName` / `hoodName` into `initialFilters`. But on the bare /search route
 * it is whatever the query string holds, and Google-indexed legacy URLs plus
 * the /search SearchAction JSON-LD point crawlers and users at slug-shaped
 * values (`?description=san-pedro-garza-garcia`). Printed verbatim those leak
 * as "cerca de san-pedro-garza-garcia" instead of "cerca de San Pedro Garza
 * García".
 *
 * Resolution order:
 *   1. Known city slug -> SEARCH_PAGE_CITY_MAP[slug].cityName (accents intact)
 *   2. Known hood slug -> HOOD_MAP[slug].hoodName
 *   3. Slug-shaped free text (contains "-") -> title-cased humanization
 *   4. Already-human text -> returned unchanged
 *
 * Pure and deterministic (no `Intl` / locale APIs) so it is hydration-safe to
 * call during SSR and CSR alike.
 */
export function toLocationDisplayName(
  value: string | string[] | null | undefined
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value
  const trimmed = raw?.trim()
  if (!trimmed) return undefined

  // Normalize to the slug shape our maps are keyed by — strip diacritics,
  // lowercase, drop stray leading/trailing hyphens. Mirrors
  // FilterProvider.toSlug so an accented Google-indexed variant
  // ("san-pedro-garza-garcía") or a comma-bearing legacy slug
  // ("guadalajara-") still hits the map.
  const slug = trimmed
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/^-+|-+$/g, '')

  if (Object.prototype.hasOwnProperty.call(SEARCH_PAGE_CITY_MAP, slug)) {
    return SEARCH_PAGE_CITY_MAP[slug as SearchPageCity].cityName
  }
  if (Object.prototype.hasOwnProperty.call(HOOD_MAP, slug)) {
    return HOOD_MAP[slug as SearchHood].hoodName
  }

  // Not a known place. Only reshape slug-shaped input; leave real names
  // (already spaced/accented, e.g. free-text "colonia roma") untouched.
  // Accents can't be recovered from an unknown slug, but every real SpotMe
  // city/hood is covered by the maps above.
  if (!trimmed.includes('-')) return trimmed
  return trimmed
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

/**
 * Slug shape produced by `canonicalCity` / `canonicalHood` and by every
 * `SearchPageCity` / `SearchHood` enum member: lowercase, unaccented,
 * hyphen-joined, no spaces or punctuation.
 */
const SLUG_SHAPE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * Display value for the nav search bar's "DÓNDE" input.
 *
 * The input is seeded from `filters.search ?? filters.description`, and on
 * `/search` those params are frequently the raw enum slug rather than a
 * human place name: `SearchManager` spreads `LocationsProvider`'s `mode`
 * (whose `search`/`description` come from `canonicalCity`, i.e. a
 * `SearchPageCity` value) into `updateParams` on pagination and on every map
 * pan, and the pillar city/hood → `/search` hop carries the same values.
 * Result: the bar reads "san-pedro-garza-garcia" after the page loads.
 *
 * Narrower than {@link toLocationDisplayName} on purpose — it only reshapes
 * values that are unambiguously slug-shaped, so a Google Places description
 * that happens to contain a hyphen ("Cuauhtémoc - Centro") is left exactly as
 * the renter saw it when they picked it. `search` / `description` are
 * display-and-analytics only (the backend query runs on placeId / center /
 * filters), so humanizing here cannot change which listings come back.
 */
export function toSearchInputValue(
  value: string | string[] | null | undefined
): string {
  const raw = Array.isArray(value) ? value[0] : value
  const trimmed = raw?.trim()
  if (!trimmed) return ''
  if (!SLUG_SHAPE_RE.test(trimmed)) return trimmed
  return toLocationDisplayName(trimmed) ?? trimmed
}
